import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { TitleProps, Title } from "./Title";
import { SimpleRichTextProps, SimpleRichText } from "./SimpleRichText";
import { ChevronIcon } from "./ChevronIcon";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import { Link } from "./Link";
import {
  IconProps,
  ImageWithAspectRatioProps,
  LinkProps,
  CardBackgroundColorType,
  CardPaddingSizeType,
} from "./types";

export const cardPlans = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:gap-6 uipm:h-full",
    "uipm:font-normal",
    "uipm:rounded-none uipm:md:rounded-[var(--radius-lg)]",
  ),
  variants: {
    backgroundColor: {
      primary: "uipm:bg-[var(--surface-surface-accent)]",
      secondary: "uipm:bg-[var(--surface-surface-accent-secondary)]",
      "secondary-light": "uipm:bg-[var(--surface-surface-accent-light)]",
      light: "uipm:bg-[var(--surface-surface-secondary)]",
      "very-light": "uipm:bg-[#F7F7F7]",
      white: "uipm:bg-[var(--surface-surface-primary)]",
      none: "uipm:bg-transparent",
    },
    verticalPadding: {
      default: "uipm:py-8 uipm:md:py-[var(--padding-padding-m)]",
      small: "uipm:py-[var(--container-card-padding)]",
      medium: "uipm:py-[var(--container-card-medium-padding)]",
      large: "uipm:py-[var(--container-card-large-padding)]",
    },
    horizontalPadding: {
      default: "uipm:px-4 uipm:md:px-[var(--padding-padding-m)]",
      small: "uipm:px-[var(--container-card-padding)]",
      medium: "uipm:px-[var(--container-card-medium-padding)]",
      large: "uipm:px-[var(--container-card-large-padding)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
    verticalPadding: "default",
    horizontalPadding: "default",
  },
});

const subtitleWrapper = tv({
  base: "uipm:font-normal",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted-faded)]",
      secondary: "uipm:text-[var(--typography-colors-text-accent)]",
      "secondary-light": "uipm:text-[var(--typography-colors-text-accent)]",
      light: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      "very-light": "uipm:text-[var(--typography-colors-text-faded-dark)]",
      white: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      none: "uipm:text-[var(--typography-colors-text-faded-dark)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

const textWrapper = tv({
  base: "uipm:font-normal",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted-faded)]",
      secondary: "uipm:text-[var(--typography-colors-text-accent)]",
      "secondary-light": "uipm:text-[var(--typography-colors-text-accent)]",
      light: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      "very-light": "uipm:text-[var(--typography-colors-text-faded-dark)]",
      white: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      none: "uipm:text-[var(--typography-colors-text-faded-dark)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

const linkWrapper = tv({
  base: "uipm:font-semibold uipm:flex uipm:items-center uipm:gap-2",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted)]",
      secondary: "uipm:text-[var(--typography-colors-link)]",
      "secondary-light": "uipm:text-[var(--typography-colors-link)]",
      light: "uipm:text-[var(--typography-colors-link)]",
      "very-light": "uipm:text-[var(--typography-colors-link)]",
      white: "uipm:text-[var(--typography-colors-link)]",
      none: "uipm:text-[var(--typography-colors-link)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

export interface CardPlansProps extends Omit<ComponentProps<"div">, "title"> {
  iconTitle?: TitleProps | null;
  icon?: ImageWithAspectRatioProps | null;
  title?: TitleProps | null;
  subtitle?: SimpleRichTextProps | null;
  text?: SimpleRichTextProps | null;
  link?: LinkProps | null;
  backgroundColor?: CardBackgroundColorType;
  verticalPadding?: CardPaddingSizeType;
  horizontalPadding?: CardPaddingSizeType;
}

export const CardPlans: React.FC<CardPlansProps> = ({
  iconTitle,
  icon,
  title,
  subtitle,
  text,
  link,
  backgroundColor = "primary",
  verticalPadding = "default",
  horizontalPadding = "default",
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        cardPlans({ backgroundColor, verticalPadding, horizontalPadding }),
        className,
      )}
      {...props}
    >
      {/* Content wrapper that grows */}
      <div className="uipm:flex uipm:flex-col uipm:gap-6 uipm:flex-grow">
        {/* Icon + IconTitle Section */}
        {(icon || iconTitle) && (
          <div className={"uipm:flex uipm:items-center uipm:gap-4"}>
            {icon && (
              <div
                className={cn(
                  "uipm:w-11 uipm:h-11 uipm:flex-shrink-0",
                  "uipm:md:w-12 uipm:md:h-12",
                )}
              >
                <ImageWithAspectRatio {...icon} sizes="48px" />
              </div>
            )}
            {iconTitle && (
              <Title {...iconTitle} className={iconTitle.className} />
            )}
          </div>
        )}

        {/* Title + Subtitle Section */}
        {(title || subtitle) && (
          <div className="uipm:flex uipm:flex-col">
            {title && <Title {...title} className={title.className} />}
            {subtitle && (
              <div className={subtitleWrapper({ backgroundColor })}>
                <SimpleRichText {...subtitle} />
              </div>
            )}
          </div>
        )}

        {/* Text Section */}
        {text && (
          <div className={textWrapper({ backgroundColor })}>
            <SimpleRichText {...text} />
          </div>
        )}
      </div>

      {/* Link Section - Always at bottom */}
      {link && (
        <Link
          href={link.href}
          target={link.target}
          noFollow={link.noFollow}
          className={linkWrapper({ backgroundColor })}
        >
          <span>{link.text}</span>
          <ChevronIcon className={linkWrapper({ backgroundColor })} />
        </Link>
      )}
    </div>
  );
};

export type CardPlansVariants = VariantProps<typeof cardPlans>;
